import {
    type CSSProperties,
    useEffect,
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    Progress,
    Button,
    Switch,
    Text
} from "ncore-ui-kit";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";
import {
    CheckIcon,
    InfoIcon
} from "../../anatomy/icons";

const NAME = "Progress";

const USAGE_CODE = `<Progress
    description="1.2 MB"
    title="report.pdf"
    value={64}
/>

<Progress
    variant="segmented"
    title="Setup"
    steps={5}
    value={3}
/>

<Progress
    variant="circular"
    title="Import"
    size="large"
    value={64}
/>

<Progress
    isIndeterminate={true}
    title="Preparing"
/>`;

const COMPOSITION_CODE = `<Progress
    valueFormatter={() => "Done"}
    description="File uploaded"
    type="success"
    title="Upload"
    value={100}
>
    <Progress.Header>
        <Progress.Title/>
        <Progress.Value
            icon={CheckIcon}
        />
    </Progress.Header>
    <Progress.Track/>
    <Progress.Description/>
</Progress>

<Progress
    valueFormatter={() => "92% full"}
    title="Storage"
    type="warning"
    isMeter={true}
    value={92}
/>`;

const TYPES = [
    "primary",
    "neutral",
    "success",
    "warning",
    "danger",
    "info"
] as const;

const SIZES = [
    "small",
    "medium",
    "large"
] as const;

const FULL = 100;

const STEP = 12;

const INTERVAL = 700;

const INITIAL_VALUE = 64;

const CELL_STYLE: CSSProperties = {
    width: 220
};

const ITEM_STYLE: CSSProperties = {
    flexDirection: "column",
    alignItems: "center",
    display: "flex",
    gap: 8
};

const CENTER_STYLE: CSSProperties = {
    justifyContent: "center",
    display: "flex"
};

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("section.variants"),
        id: "variants"
    },
    {
        title: t("progressPage.typesTitle"),
        id: "types"
    },
    {
        title: t("section.sizes"),
        id: "sizes"
    },
    {
        title: t("progressPage.indeterminateTitle"),
        id: "indeterminate"
    },
    {
        title: t("progressPage.compositionTitle"),
        id: "composition"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const ProgressPage = () => {
    const {
        t
    } = useDocsI18n();

    const [
        value,
        setValue
    ] = useState(INITIAL_VALUE);

    const [
        isRunning,
        setIsRunning
    ] = useState(false);

    const [
        isIndeterminate,
        setIsIndeterminate
    ] = useState(false);

    useEffect(() => {
        if(!isRunning) {
            return;
        }

        const timer = window.setInterval(() => setValue((current) => Math.min(FULL, current + STEP)), INTERVAL);

        return () => {
            window.clearInterval(timer);
        };
    }, [
        isRunning
    ]);

    useEffect(() => {
        if(value >= FULL) {
            setIsRunning(false);
        }
    }, [
        value
    ]);

    const handleRestart = () => {
        setValue(0);

        setIsRunning(true);
    };

    const handleToggleIndeterminate = () => setIsIndeterminate(!isIndeterminate);

    return <Article>
        <ComponentHeader
            lead={t("progressPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("progressPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("progressPage.overviewCaption")}
                isStacked={true}
            >
                <Progress
                    description={t("sample.fileSize")}
                    title={t("sample.fileName")}
                    value={value}
                />
                <Button
                    title={t("progressPage.restart")}
                    onPress={handleRestart}
                    isDisabled={isRunning}
                    variant="outline"
                    type="neutral"
                    size="small"
                />
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("section.variants")}
            id="variants"
        >
            <Paragraph>
                {t("progressPage.variantsText")}
            </Paragraph>
            <Preview>
                <div
                    style={CELL_STYLE}
                >
                    <Progress
                        title={t("progressPage.import")}
                        value={64}
                    />
                </div>
                <div
                    style={CELL_STYLE}
                >
                    <Progress
                        title={t("progressPage.setup")}
                        variant="segmented"
                        steps={5}
                        value={3}
                    />
                </div>
                <Progress
                    title={t("progressPage.import")}
                    variant="circular"
                    size="large"
                    value={64}
                />
            </Preview>
        </Section>
        <Section
            title={t("progressPage.typesTitle")}
            id="types"
        >
            <Paragraph>
                {t("progressPage.typesText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                {
                    TYPES.map((type, index) => <Progress
                        value={40 + index * 10}
                        title={type}
                        type={type}
                        key={type}
                    />)
                }
            </Preview>
        </Section>
        <Section
            title={t("section.sizes")}
            id="sizes"
        >
            <Paragraph>
                {t("progressPage.sizesText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                {
                    SIZES.map((size) => <Progress
                        title={size}
                        size={size}
                        key={size}
                        value={64}
                    />)
                }
            </Preview>
            <Preview>
                {
                    SIZES.map((size) => <div
                        style={ITEM_STYLE}
                        key={size}
                    >
                        <Progress
                            title={t("progressPage.import")}
                            variant="circular"
                            size={size}
                            value={64}
                        />
                        <Text
                            variant="bodySmallSize"
                            color="low"
                        >
                            {`size="${size}"`}
                        </Text>
                    </div>)
                }
            </Preview>
        </Section>
        <Section
            title={t("progressPage.indeterminateTitle")}
            id="indeterminate"
        >
            <Paragraph>
                {t("progressPage.indeterminateText")}
            </Paragraph>
            <Preview
                caption={t("progressPage.indeterminateCaption")}
                isStacked={true}
            >
                <Progress
                    title={t("progressPage.preparing")}
                    isIndeterminate={isIndeterminate}
                    value={40}
                />
                <div
                    style={CENTER_STYLE}
                >
                    <Progress
                        title={t("progressPage.preparing")}
                        isIndeterminate={isIndeterminate}
                        variant="circular"
                        size="large"
                        value={40}
                    />
                </div>
                <Switch
                    title={t("progressPage.indeterminateSwitch")}
                    onPress={handleToggleIndeterminate}
                    isChecked={isIndeterminate}
                />
            </Preview>
        </Section>
        <Section
            title={t("progressPage.compositionTitle")}
            id="composition"
        >
            <Paragraph>
                {t("progressPage.compositionText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <Progress
                    valueFormatter={() => t("progressPage.done")}
                    description={t("progressPage.doneText")}
                    title={t("progressPage.upload")}
                    type="success"
                    value={FULL}
                >
                    <Progress.Header>
                        <Progress.Title/>
                        <Progress.Value
                            icon={CheckIcon}
                        />
                    </Progress.Header>
                    <Progress.Track/>
                    <Progress.Description/>
                </Progress>
                <Progress
                    valueFormatter={() => t("progressPage.failed")}
                    description={t("progressPage.failedText")}
                    title={t("progressPage.upload")}
                    type="danger"
                    value={48}
                >
                    <Progress.Header/>
                    <Progress.Track/>
                    <Progress.Description
                        icon={InfoIcon}
                    />
                </Progress>
                <Progress
                    valueFormatter={() => t("progressPage.storageValue")}
                    description={t("progressPage.storageHelp")}
                    title={t("progressPage.storage")}
                    type="warning"
                    isMeter={true}
                    value={92}
                />
            </Preview>
            <CodeBlock
                code={COMPOSITION_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("progressPage.a11yRole"),
                    t("progressPage.a11yName"),
                    t("progressPage.a11yLive"),
                    t("progressPage.a11yValueHidden"),
                    t("progressPage.a11yMotion"),
                    t("progressPage.a11yParts")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("progressPage.propValue"),
                        defaultValue: "0",
                        type: "number",
                        name: "value"
                    },
                    {
                        description: t("progressPage.propMax"),
                        defaultValue: "100",
                        type: "number",
                        name: "max"
                    },
                    {
                        description: t("progressPage.propSteps"),
                        type: "number",
                        name: "steps"
                    },
                    {
                        type: "\"linear\" | \"segmented\" | \"circular\"",
                        description: t("progressPage.propVariant"),
                        defaultValue: "\"linear\"",
                        name: "variant"
                    },
                    {
                        type: "\"primary\" | \"neutral\" | \"success\" | \"warning\" | \"danger\" | \"info\"",
                        description: t("progressPage.propType"),
                        defaultValue: "\"primary\"",
                        name: "type"
                    },
                    {
                        type: "\"small\" | \"medium\" | \"large\"",
                        description: t("progressPage.propSize"),
                        defaultValue: "\"medium\"",
                        name: "size"
                    },
                    {
                        description: t("progressPage.propTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("progressPage.propDescription"),
                        name: "description",
                        type: "string"
                    },
                    {
                        description: t("progressPage.propIsValueVisible"),
                        name: "isValueVisible",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("progressPage.propIsIndeterminate"),
                        name: "isIndeterminate",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("progressPage.propIsMeter"),
                        defaultValue: "false",
                        name: "isMeter",
                        type: "boolean"
                    },
                    {
                        description: t("progressPage.propValueFormatter"),
                        type: "(value: number, max: number) => string",
                        name: "valueFormatter"
                    },
                    {
                        description: t("progressPage.propChildren"),
                        type: "ReactNode",
                        name: "children"
                    },
                    {
                        description: t("progressPage.propAccessibilityLabel"),
                        name: "accessibilityLabel",
                        type: "string"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("progressPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("progressPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("progressPage.propsNote")}
            </Paragraph>
            <Paragraph>
                {t("progressPage.partsText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("progressPage.propPartChildren"),
                        type: "ReactNode",
                        name: "children"
                    },
                    {
                        description: t("progressPage.propPartIcon"),
                        type: "NCoreUIKitIcon",
                        name: "icon"
                    },
                    {
                        description: t("progressPage.propPartIsDone"),
                        defaultValue: "false",
                        name: "isDone",
                        type: "boolean"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("progressPage.propPartCustomTheme"),
                        name: "customTheme"
                    }
                ]}
            />
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default ProgressPage;
